import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { profile, axiosConfig } from 'api';
import { AxiosResponse } from 'axios';
import { RootState, MyThunkDispatch } from 'store';

import { fetchHasAccess } from 'actions/accessLog';
import useSelectionOrigin from 'hooks/useSelectionOrigin';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { Group } from 'models/group';
import { GroupedListModel } from 'models/groupedList';
import GroupSelectList from 'screens/components/GroupSelectList';

interface Props {
  showGroupsSelection: boolean;
  currentGroup: GroupedListModel[];
  groupTarget?: string;
  selectedGroupsRules?: any;
  onConfirmSelection: (group: GroupedListModel[]) => void;
  onCloseSelectionScreen: () => void;
  changeModalTitleKey: string;
  changeModalMessageKey: string;
}

const GroupListContainer = ({
  showGroupsSelection,
  onConfirmSelection,
  onCloseSelectionScreen,
  currentGroup,
  groupTarget,
  selectedGroupsRules,
  changeModalTitleKey,
  changeModalMessageKey,
}: Props) => {
  const dispatch: MyThunkDispatch = useDispatch();
  const [groupList, setGroupList] = useState<GroupedListModel[]>([]);
  const [isLoading, setLoading] = useState(false);
  const toast = useToast();
  const { t } = useStoreTranslation();
  const { itemCreated } = useSelectionOrigin();
  const companyId = useSelector((state: RootState) => state.user.accountResult?.companyId);
  const adminId = useSelector((state: RootState) => (state.user.accountResult ? state.user.accountResult?.id : ''));

  const tourLoading = useSelector((state: RootState) => state.accessLog.isLoading);
  const hasAccess = useSelector((state: RootState) => state.accessLog.ListGroups);

  useEffect(() => {
    const screenName = 'ListGroups';
    dispatch(fetchHasAccess(screenName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminId]);

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const {
          data: { data },
        } = await profile.get<AxiosResponse<Group[]>>('/UserGroup/GetAll', axiosConfig);
        setGroupList(
          data.map((group: Group) => ({
            id: group.id,
            name: group.name,
            badge: !!group.rule ? { id: group.rule.id, name: group.rule.name } : { id: '', name: '' },
          })),
        );
      } catch (error) {
        toast.add({
          type: 'danger',
          message: t('group.groupAggregation.errorFetch'),
        });
        setLoading(false);
      }
      setLoading(false);
    };

    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, itemCreated]);

  return (
    <GroupSelectList
      isAlphabetic
      showGroupsSelection={showGroupsSelection}
      groupList={groupList}
      isLoading={isLoading}
      tourHasAccess={hasAccess}
      tourLoading={tourLoading}
      onConfirmSelection={onConfirmSelection}
      onCloseSelectionScreen={onCloseSelectionScreen}
      currentGroup={currentGroup}
      groupTarget={groupTarget}
      selectedGroupsRules={selectedGroupsRules}
      changeModalTitleKey={changeModalTitleKey}
      changeModalMessageKey={changeModalMessageKey}
    />
  );
};

export default GroupListContainer;
